import { useEffect, useState } from 'react'

import { useCommercialStructure } from 'commons/customHooks'
import { usePermissions } from 'commons/customHooks/useAuth'
import { downloadFile, FileType } from 'commons/utils/file'
import { formatToPTBRNumber, numberToDecimalString } from 'commons/utils/number'
import { isObjectEmpty } from 'commons/utils/object'
import { ACTIONS_SCREENS, SCREENS } from 'commons/utils/permissions'
import {
  Alert,
  DSInputType,
  EmptyContent,
  type HeadItemType,
  Icon,
  Input,
  Modal,
  Page,
  SlimButton,
  Table,
  ToggleButton,
  Typography,
} from 'components'
import { Offices, officeByMarkets } from 'constants/enums'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { downloadRangeExcel } from 'services/backoffice'
import type { TeamRange } from 'services/backoffice/interfaces'
import {
  dispatchRangeReview,
  dispatchRangeUpdate,
  dispatchRangeUpdatePatch,
  dispatchRangeVisualization,
  dispatchSendRange,
  getTeamRanges,
} from 'store/BackofficeContext/backofficeSlice'
import { getCommercialStructure } from 'store/SharedContext/sharedSlice'
import { useDispatch, useSelector } from 'store/store'
import { theme } from 'styles/theme/hav-theme'

import { TopBar } from './components/TopBar'
import TrackingModal from './components/TrackingModal'
import * as S from './styles'
import { validateCurrency, validatePercentage } from './utils'

type EditableValues = {
  [x: string]: { maxPercentage: number; maxAmount: number }
}

const BackofficeRanges = () => {
  const dispatch = useDispatch()
  const { verifyPermission } = usePermissions()
  const { getDirectorByOffice, hasCommercialStructure } = useCommercialStructure()
  const { offices: userOffices } = useSelector((state) => state.auth)
  const { teamRanges, rangeVersion, loadingData } = useSelector((state) => state.backoffice)

  const { control, setValue, handleSubmit } = useForm<EditableValues>()
  const [selectedOffice, setSelectedOffice] = useState(
    userOffices.length ? userOffices[0] : Offices.AC60,
  )
  const [isEditing, setIsEditing] = useState(false)
  const [approveModal, setApproveModal] = useState(false)
  const [rejectModal, setRejectModal] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [openTracking, setOpenTracking] = useState(false)

  const canEdit = verifyPermission(SCREENS.BACKOFFICE_RANGE, ACTIONS_SCREENS.SAVE)
  const canApprove = verifyPermission(SCREENS.BACKOFFICE_RANGE, ACTIONS_SCREENS.APPROVE)
  const canReject = verifyPermission(SCREENS.BACKOFFICE_RANGE, ACTIONS_SCREENS.DISMISS)
  const canReview = (canApprove || canReject) && userOffices.includes(selectedOffice)

  const rangeStatus = {
    analysis: 30,
    approved: 100,
    pending: 20,
    rejected: 90,
  }

  const handleEdit = () => {
    setIsEditing(true)
    setPendingFields()
  }

  const getStatusColor = (status: number) => {
    if (status === rangeStatus.approved) return theme.colors.alert.success[40]

    if ([rangeStatus.pending, rangeStatus.analysis].includes(status)) {
      return theme.colors.alert.warning[40]
    }

    if (status === rangeStatus.rejected) return theme.colors.alert.danger[40]

    return theme.colors.neutral.high
  }

  const getPercentValue = (range: TeamRange) => {
    const changedTeams = rangeVersion?.Regionais?.map((team) => team.Codigo)

    if (changedTeams?.includes(range.CodigoEquipe) && !rangeVersion.Visualizacao) {
      const teamVersion = rangeVersion?.Regionais?.find(
        (team) => team.Codigo === range.CodigoEquipe,
      )

      if (teamVersion && (canEdit || rangeVersion.Status.Codigo !== rangeStatus.pending)) {
        const prevVal = teamVersion.PorcentagemAnterior
        const propVal = teamVersion.Porcentagem

        if (prevVal !== propVal) {
          return (
            <S.ColoredText color={getStatusColor(rangeVersion.Status.Codigo)}>
              {`${numberToDecimalString(prevVal)}% -> ${numberToDecimalString(propVal)}%`}
            </S.ColoredText>
          )
        }
      }
    }

    return `${numberToDecimalString(range.PorcentagemMaxima)}%`
  }

  const getCurrencyValue = (range: TeamRange) => {
    const changedTeams = rangeVersion?.Regionais?.map((team) => team.Codigo)

    if (changedTeams.includes(range.CodigoEquipe) && !rangeVersion.Visualizacao) {
      const teamVersion = rangeVersion?.Regionais?.find(
        (team) => team.Codigo === range.CodigoEquipe,
      )

      if (teamVersion && (canEdit || rangeVersion?.Status?.Codigo !== rangeStatus.pending)) {
        const prevVal = teamVersion.ValorAnterior
        const propVal = teamVersion.Valor

        if (prevVal !== propVal) {
          return (
            <S.ColoredText color={getStatusColor(rangeVersion?.Status?.Codigo)}>
              {`R$${numberToDecimalString(prevVal)} ->
              R$${numberToDecimalString(propVal)}`}
            </S.ColoredText>
          )
        }
      }
    }

    return `${formatToPTBRNumber(range.ValorMaximo)}`
  }

  const tableHead: HeadItemType[] = [
    { columnName: 'Regional', label: 'Regional' },
    { columnName: 'NomeRegional', label: 'Nome regional' },
    { columnName: 'Responsavel', label: 'Responsável' },
    { columnName: 'PorcentagemMaxAlcada', label: '% máx. Alçada' },
    { columnName: 'ValorMaxAlcada', label: 'R$ máx. Alçada' },
  ]

  const tableBody = () => {
    if (loadingData) return []

    const body = teamRanges?.map((item) => {
      const percentField = () => {
        if (isEditing) {
          return (
            <Controller
              control={control}
              name={`[${item.CodigoEquipe}].maxPercentage`}
              render={({ field }) => (
                <Input
                  addorment
                  design={DSInputType.PERCENT_INPUT}
                  height={28}
                  onChange={(e) => {
                    const value = e.target.value
                    if (validatePercentage(value)) field.onChange(value)
                  }}
                  value={field.value}
                  width={120}
                />
              )}
            />
          )
        }

        return getPercentValue(item)
      }

      const amountField = () => {
        if (isEditing) {
          return (
            <Controller
              control={control}
              name={`[${item.CodigoEquipe}].maxAmount`}
              render={({ field }) => (
                <Input
                  addorment
                  design={DSInputType.CURRENCY_INPUT}
                  height={28}
                  maxLength={6}
                  onChange={(e) => {
                    const value = e.target.value
                    if (validateCurrency(value)) field.onChange(value)
                  }}
                  value={field.value}
                  width={120}
                />
              )}
            />
          )
        }

        return getCurrencyValue(item)
      }

      return {
        content: [
          item.CodigoEquipe,
          item.NomeEquipe ?? '-',
          item.ResponsavelEquipe,
          percentField(),
          amountField(),
        ],
        id: item.CodigoEquipe,
      }
    })

    return body
  }

  const resetFields = () => {
    teamRanges.forEach((item) => {
      setValue(item.CodigoEquipe, {
        maxAmount: item.ValorMaximo,
        maxPercentage: item.PorcentagemMaxima,
      })
    })
  }

  const setPendingFields = () => {
    if (rangeVersion?.Regionais?.length > 0 && !rangeVersion?.Visualizacao) {
      rangeVersion?.Regionais?.forEach((item) => {
        setValue(item.Codigo, {
          maxAmount: item.Valor,
          maxPercentage: item.Porcentagem,
        })
      })
    }
  }

  const handleCancelEdit = () => {
    resetFields()
    setIsEditing(false)
  }

  const onSubmit = (data: EditableValues) => {
    setIsEditing(false)
    const teams = Object.keys(data).map((key) => {
      return {
        codigoEquipe: key,
        percentualDescontoNovo: data[key].maxPercentage,
        valorDescontoNovo: data[key].maxAmount,
      }
    })

    const dirtyTeams = teams.filter(
      (team) =>
        typeof team.valorDescontoNovo === 'string' ||
        typeof team.percentualDescontoNovo === 'string',
    )

    const emptyValues = dirtyTeams.filter(
      (team) => `${team.valorDescontoNovo}` === '' || `${team.percentualDescontoNovo}` === '',
    )
    if (emptyValues.length > 0) {
      resetFields()
      toast.error('Não foi possível salvar as suas alterações devido à presença de campos vazios.')
      return
    }

    const formattedTeams = dirtyTeams.map((team) => {
      return {
        codigoEquipe: team.codigoEquipe,
        percentualDescontoNovo: Number(`${team.percentualDescontoNovo}`.replace(',', '.')), // TODO: Create utils function to replace decimal separator
        valorDescontoNovo: Number(`${team.valorDescontoNovo}`.replace(',', '.')), // TODO: Create utils function to replace decimal separator
      }
    })

    if (rangeVersion.Status.Codigo === rangeStatus.pending) {
      const payload = {
        alcadas: formattedTeams,
        codigoVersao: rangeVersion.CodigoVersao,
      }

      dispatch(dispatchRangeUpdatePatch(payload, selectedOffice))
    } else {
      const hasChanges = formattedTeams.some((team) => {
        const teamVersion = teamRanges.find((range) => range.CodigoEquipe === team.codigoEquipe)

        return (
          teamVersion &&
          (teamVersion.ValorMaximo !== team.valorDescontoNovo ||
            teamVersion.PorcentagemMaxima !== team.percentualDescontoNovo)
        )
      })

      if (!hasChanges) {
        resetFields()
        return
      }

      const payload = {
        alcadas: formattedTeams,
        escritorio: selectedOffice,
      }

      dispatch(dispatchRangeUpdate(payload, selectedOffice))
    }
  }

  const handleVisualization = () => {
    dispatch(dispatchRangeVisualization(rangeVersion.CodigoVersao, selectedOffice))
  }

  const handleSend = () => {
    dispatch(dispatchSendRange(rangeVersion.CodigoVersao, selectedOffice))
  }

  const handleReview = (approval: boolean) => {
    const payload = {
      aprovado: approval,
      codigoVersao: rangeVersion.CodigoVersao,
      observacao: !approval ? rejectReason : '',
    }

    dispatch(dispatchRangeReview(payload, selectedOffice))
    setRejectReason('')
  }

  useEffect(() => {
    if (!hasCommercialStructure) dispatch(getCommercialStructure())
  }, [hasCommercialStructure])

  useEffect(() => {
    resetFields()
  }, [teamRanges])

  useEffect(() => {
    dispatch(getTeamRanges(selectedOffice))
  }, [selectedOffice])

  const renderToggleButtons = () => {
    const officesMI = officeByMarkets.MI

    return officesMI.map((office) => (
      <ToggleButton
        active={selectedOffice === office}
        content={`Escritório ${office}`}
        disabled={loadingData || isEditing}
        key={office}
        onClick={() => setSelectedOffice(office)}
      />
    ))
  }

  const renderDirectorName = () => {
    const director = getDirectorByOffice(selectedOffice as Offices)
    if (!director) return null
    return (
      <Typography design="hav-default-04-text">
        Responsável por <strong>{director}</strong>
      </Typography>
    )
  }

  const handleDownload = async () => {
    toast.info('O download começará em instantes.')

    try {
      const file = await downloadRangeExcel()

      downloadFile({
        file,
        fileName: 'planilha-alçadas.xlsx',
        fileType: FileType.Excel,
      })
    } catch (error) {
      toast.error('Houve um problema ao iniciar o download. Tente novamente mais tarde')
    }
  }

  const renderTopBar = () => {
    return (
      <TopBar
        canEdit={canEdit}
        disabled={loadingData}
        editButtonDisabled={!teamRanges.length}
        isEditing={isEditing}
        isWaitingForApproval={rangeVersion.Status?.Codigo === rangeStatus.analysis}
        onCancelEditClick={handleCancelEdit}
        onDownloadClick={handleDownload}
        onEditClick={handleEdit}
        onSaveEditClick={handleSubmit(onSubmit)}
        onTrackingClick={() => setOpenTracking(true)}
      />
    )
  }

  const renderAlerts = () => {
    if (!loadingData && !isObjectEmpty(rangeVersion)) {
      const isPending = rangeVersion.Status.Codigo === rangeStatus.pending
      const isApproved = rangeVersion.Status.Codigo === rangeStatus.approved
      const isRejected = rangeVersion.Status.Codigo === rangeStatus.rejected
      const isAnalysis = rangeVersion.Status.Codigo === rangeStatus.analysis // NOTE: Waiting for approval

      return (
        <>
          {canEdit && isPending && (
            <Alert
              actionIcon={<Icon icon="send" size="15" />}
              actionLabel="Solicitar aprovação"
              fullWidth
              handleAction={handleSend}
              kind="info"
              title="Alterações realizadas! Não esqueça de solicitar aprovação."
            />
          )}

          {canEdit && !rangeVersion.Visualizacao && isApproved && (
            <Alert
              actionLabel="Ok"
              fullWidth
              handleAction={handleVisualization}
              kind="success"
              title="Atualização de política de alçadas aprovada e vigente."
            />
          )}
          {canEdit && !rangeVersion.Visualizacao && isRejected && (
            <Alert
              actionLabel="Ok"
              fullWidth
              handleAction={handleVisualization}
              kind="error"
              message={`Justificativa: ${rangeVersion.ObservacaoAnalise}`}
              title="Atualização de política de alçadas reprovada."
            />
          )}
          {canEdit && isAnalysis && (
            <Alert
              fullWidth
              kind="warning"
              title=" Alterações pendentes de aprovação da diretoria comercial."
            />
          )}

          {canReview && isAnalysis && (
            <Alert fullWidth kind="warning" title="Alterações pendentes de aprovação." />
          )}
        </>
      )
    }
  }

  return (
    <Page fullwidth loading={loadingData} renderHeaderComponent={renderTopBar()}>
      <S.Content>
        {renderAlerts()}

        <S.Card>
          <S.TitleContainer>
            <S.Title>Política de Alçadas</S.Title>
            <Typography design="hav-h7-text">
              {canEdit ? 'Defina' : 'Confira abaixo'} a política de alçadas para aprovação de
              negociações diferenciadas por percentual e limite de valor para cada Regional.
            </Typography>
          </S.TitleContainer>
          <S.ButtonsRow>{renderToggleButtons()}</S.ButtonsRow>
          <S.OfficeTitle>
            <Typography className="office-title" design="hav-h5-text">
              Escritório {selectedOffice}
            </Typography>
            {renderDirectorName()}
          </S.OfficeTitle>

          {!teamRanges.length ? (
            <EmptyContent
              description="Não foi possível encontrar a configuração de política de alçadas do escritório selecionado."
              title="Configuração de alçadas indisponível"
            />
          ) : (
            <Table bodyData={tableBody()} headData={tableHead} rowHeight={50} />
          )}

          {!loadingData && !!teamRanges.length && (
            <S.TableActions>
              {canReview && rangeVersion.Status?.Codigo === rangeStatus.analysis && (
                <S.ActionButtons>
                  <SlimButton
                    content="Reprovar"
                    disabled={!canReject}
                    icon={<Icon icon="cancel" size="15" />}
                    onClick={() => setRejectModal(true)}
                    variant="danger"
                  />
                  <SlimButton
                    content="Aprovar"
                    disabled={!canApprove}
                    icon={<Icon icon="task-alt" size="15" />}
                    onClick={() => setApproveModal(true)}
                    variant="success"
                  />
                </S.ActionButtons>
              )}
            </S.TableActions>
          )}
        </S.Card>
      </S.Content>
      <Modal
        buttonAction={() => {
          setApproveModal(false)
          handleReview(true)
        }}
        buttonIcon={<Icon icon="task-alt" size="15" />}
        buttonText="Aprovar"
        buttonVariant="success"
        closeAction={() => setApproveModal(false)}
        description="Tem certeza que deseja aprovar a atualização de política de alçadas?"
        modalWidth={456}
        open={approveModal}
        returnAction={() => setApproveModal(false)}
        returnText="Voltar"
        setOpen={setApproveModal}
        showClose={false}
        showReturn
        title="Aprovar atualização"
      />
      <Modal
        buttonAction={() => {
          setRejectModal(false)
          handleReview(false)
        }}
        buttonDisabled={!rejectReason}
        buttonIcon={<Icon icon="cancel" size="15" />}
        buttonText="Reprovar"
        buttonVariant="danger"
        closeAction={() => {
          setRejectReason('')
          setRejectModal(false)
        }}
        description="Insira uma justificativa para a reprovação."
        modalWidth={456}
        open={rejectModal}
        returnAction={() => setRejectModal(false)}
        returnText="Voltar"
        setOpen={setRejectModal}
        showClose={false}
        showReturn
        title="Reprovar atualização"
      >
        <S.ModalRejectReason>
          <Input
            height={96}
            maxLength={255}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Descreva aqui a justificativa"
            required
            rows={3}
            showMaxLength
            value={rejectReason}
          />
        </S.ModalRejectReason>
      </Modal>
      {openTracking && <TrackingModal setOpen={setOpenTracking} />}
    </Page>
  )
}

export default BackofficeRanges
